import { useEffect, useRef, useState } from "react";
import { Bell, BellOff, Moon, Send } from "lucide-react";
import { toast } from "sonner";
import { loadFavorites } from "./Favorites";
import { normalize } from "../lib/songMatch";
import { pushSupported, subscribeToPush, syncPushFavorites, unsubscribeFromPush, sendTestPush } from "../lib/webPush";

const PREF_KEY = "rrz-fav-notifications";
const QUIET_KEY = "rrz-fav-quiet-hours";
const QUIET_START = 22;
const QUIET_END = 7;

function isQuietNow() {
  const h = new Date().getHours();
  return h >= QUIET_START || h < QUIET_END;
}

function tokensOf(raw) {
  return normalize(raw).split(" ").filter((t) => t.length >= 3);
}

function findInUpcoming(favRaw, songs) {
  const tokens = tokensOf(favRaw);
  if (tokens.length === 0) return null;
  for (let i = 0; i < songs.length; i++) {
    const hay = normalize(songs[i]?.title || "");
    if (tokens.every((t) => hay.includes(t))) return { position: i, song: songs[i] };
  }
  return null;
}

/**
 * Web Push-first favorite alerts:
 *  - When Push is supported → subscribe with the backend; server sends
 *    notifications (works even when the app is closed).
 *  - Otherwise → in-page fallback using existing rrz:now-playing / rrz:upcoming
 *    events (only fires while a tab is open).
 */
export default function FavoriteNotifier() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(PREF_KEY) === "1"; } catch { return false; }
  });
  const [quietHours, setQuietHours] = useState(() => {
    try { return localStorage.getItem(QUIET_KEY) === "1"; } catch { return false; }
  });
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const lastNotifiedRef = useRef("");
  const upcomingNotifiedRef = useRef(new Set());
  const supportsPush = pushSupported();

  // Keep the server subscription in sync when favorites change locally.
  useEffect(() => {
    if (!enabled || permission !== "granted" || !supportsPush) return;
    const onFavChange = () => { syncPushFavorites(); };
    window.addEventListener("rrz:favorites-changed", onFavChange);
    return () => window.removeEventListener("rrz:favorites-changed", onFavChange);
  }, [enabled, permission, supportsPush]);

  // In-page fallback ONLY when push isn't supported (older browsers).
  useEffect(() => {
    if (!enabled) return;
    if (typeof Notification === "undefined") return;
    if (permission !== "granted") return;
    if (supportsPush) return; // server-driven push handles it

    const onNP = (ev) => {
      const raw = (ev?.detail?.title || "").trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (key === lastNotifiedRef.current) return;
      const favs = loadFavorites();
      const isFav = favs.some((f) => f.raw?.toLowerCase() === key);
      if (!isFav) return;
      lastNotifiedRef.current = key;
      upcomingNotifiedRef.current.delete(key);
      const silent = quietHours && isQuietNow();
      try {
        const n = new Notification("♥ Un tuo brano preferito è in onda", {
          body: raw, icon: "/icon-192.png", badge: "/icon-192.png",
          tag: "rrz-fav-playing", silent,
        });
        n.onclick = () => { try { window.focus(); n.close(); } catch { /* ignore */ } };
      } catch { /* ignore */ }
    };

    const onUp = (ev) => {
      const songs = ev?.detail?.songs || [];
      if (songs.length === 0) return;
      const favs = loadFavorites();
      if (favs.length === 0) return;
      const stillPresent = new Set();
      const silent = quietHours && isQuietNow();
      for (const fav of favs) {
        const key = fav.raw?.toLowerCase();
        if (!key) continue;
        const hit = findInUpcoming(fav.raw, songs);
        if (!hit) continue;
        stillPresent.add(key);
        if (upcomingNotifiedRef.current.has(key)) continue;
        upcomingNotifiedRef.current.add(key);
        const when = hit.position === 0 ? "Sta per iniziare" : `Fra ${hit.position + 1} brani`;
        try {
          const n = new Notification("🔜 In arrivo un tuo preferito", {
            body: `${when}: ${fav.raw}`, icon: "/icon-192.png",
            tag: `rrz-fav-upcoming-${key}`, silent,
          });
          n.onclick = () => { try { window.focus(); n.close(); } catch { /* ignore */ } };
        } catch { /* ignore */ }
      }
      for (const key of Array.from(upcomingNotifiedRef.current)) {
        if (!stillPresent.has(key)) upcomingNotifiedRef.current.delete(key);
      }
    };
    window.addEventListener("rrz:now-playing", onNP);
    window.addEventListener("rrz:upcoming", onUp);
    return () => {
      window.removeEventListener("rrz:now-playing", onNP);
      window.removeEventListener("rrz:upcoming", onUp);
    };
  }, [enabled, permission, quietHours, supportsPush]);

  const supported = typeof Notification !== "undefined";
  if (!supported) return null;

  const onToggle = async () => {
    if (enabled) {
      setEnabled(false);
      try { localStorage.setItem(PREF_KEY, "0"); } catch { /* ignore */ }
      if (supportsPush) {
        try { await unsubscribeFromPush(); } catch { /* ignore */ }
      }
      toast.info("Avvisi disattivati");
      return;
    }
    let perm = permission;
    if (perm === "default") {
      try { perm = await Notification.requestPermission(); }
      catch { perm = "denied"; }
      setPermission(perm);
    }
    if (perm !== "granted") {
      if (perm === "denied") {
        toast.error("Notifiche bloccate", {
          description: "Abilitale dalle impostazioni del browser per usare questa funzione.",
        });
      }
      return;
    }
    if (supportsPush) {
      const loadingId = toast.loading("Attivazione avvisi…");
      // Safety net — no matter what, this promise resolves within 20s.
      const timeout = new Promise((_, rej) =>
        setTimeout(() => rej(new Error("Timeout: la sottoscrizione ha superato 20 secondi")), 20000)
      );
      try {
        await Promise.race([subscribeToPush(quietHours), timeout]);
        toast.dismiss(loadingId);
        setEnabled(true);
        try { localStorage.setItem(PREF_KEY, "1"); } catch { /* ignore */ }
        toast.success("Avvisi attivi ♥ Anche a schermo spento");
      } catch (e) {
        toast.dismiss(loadingId);
        console.error("[FavoriteNotifier] subscribeToPush failed:", e);
        const errMsg = (e && e.message) || "";
        const isBrowserPushBroken =
          /google play services/i.test(errMsg) ||
          /service worker/i.test(errMsg) ||
          /timeout/i.test(errMsg);
        // On Android/iOS where Web Push is unavailable, silently degrade to
        // local-only notifications (works while the PWA is open in background).
        if (isBrowserPushBroken) {
          setEnabled(true);
          try { localStorage.setItem(PREF_KEY, "1"); } catch { /* ignore */ }
          toast.warning("Avvisi attivi (solo con app aperta)", {
            description:
              "Il tuo dispositivo non supporta le notifiche a schermo spento. Riceverai avvisi solo quando la PWA è aperta in background.",
            duration: 10000,
          });
        } else {
          toast.error("Attivazione fallita", {
            description: errMsg || "Errore sconosciuto — riprova o controlla la connessione.",
            duration: 12000,
          });
        }
      }
      return;
    }
    // Fallback: local-only notifications
    setEnabled(true);
    try { localStorage.setItem(PREF_KEY, "1"); } catch { /* ignore */ }
    toast.success("Avvisi attivi (solo con app aperta)");
  };

  const onToggleQuiet = () => {
    const next = !quietHours;
    setQuietHours(next);
    try { localStorage.setItem(QUIET_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    if (supportsPush && enabled && permission === "granted") {
      syncPushFavorites(next);
    }
    toast.info(next
      ? "Silenzio notturno attivo (22:00 – 07:00)"
      : "Silenzio notturno disattivato"
    );
  };

  const onTestPush = async () => {
    const loadingId = toast.loading("Invio notifica di prova…");
    try {
      await sendTestPush();
      toast.dismiss(loadingId);
      toast.success("Notifica inviata — dovresti riceverla tra pochi secondi");
    } catch (e) {
      toast.dismiss(loadingId);
      const detail = e?.message || "";
      const reason = e?.reason;
      // If subscription is stale, offer to re-subscribe
      if (reason === "not-found" || reason === "expired") {
        try { await unsubscribeFromPush(); } catch { /* ignore */ }
        setEnabled(false);
        try { localStorage.setItem(PREF_KEY, "0"); } catch { /* ignore */ }
        toast.error("Subscription scaduta", {
          description: "Tocca di nuovo 'Attiva avvisi' per rigenerarla.",
          duration: 10000,
        });
        return;
      }
      toast.error("Invio fallito", {
        description: detail.slice(0, 220) || "Errore sconosciuto",
        duration: 12000,
      });
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        data-testid="favorite-notifier-toggle"
        onClick={onToggle}
        aria-pressed={enabled}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition font-mono text-[10px] uppercase tracking-[0.18em] ${
          enabled
            ? "bg-[#FF6B00]/10 border-[#FF6B00]/50 text-[#FF6B00]"
            : "bg-[#1a1816] border-[#332f2b] text-[#A39A8F] hover:text-[#FF6B00] hover:border-[#FF6B00]/40"
        }`}
        title={enabled ? "Avvisi attivi" : "Attiva avvisi brani preferiti"}
      >
        {enabled ? <Bell size={14} /> : <BellOff size={14} />}
        <span className="hidden sm:inline">
          {enabled ? "Avvisi attivi" : "Attiva avvisi"}
        </span>
      </button>
      {enabled && (
        <button
          type="button"
          data-testid="favorite-notifier-quiet"
          onClick={onToggleQuiet}
          aria-pressed={quietHours}
          className={`inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border transition font-mono text-[10px] uppercase tracking-[0.18em] ${
            quietHours
              ? "bg-[#7c3aed]/15 border-[#a78bfa]/50 text-[#c4b5fd]"
              : "bg-[#1a1816] border-[#332f2b] text-[#A39A8F] hover:text-[#c4b5fd] hover:border-[#a78bfa]/40"
          }`}
          title={quietHours ? "Silenzio notturno attivo (22–7)" : "Attiva silenzio notturno"}
          aria-label="Silenzio notturno"
        >
          <Moon size={14} />
        </button>
      )}
      {enabled && supportsPush && (
        <button
          type="button"
          data-testid="favorite-notifier-test"
          onClick={onTestPush}
          className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border border-[#1E88E5]/50 bg-[#1E88E5]/10 text-[#8ec5ff] hover:bg-[#1E88E5]/20 transition font-mono text-[10px] uppercase tracking-[0.18em]"
          title="Invia una notifica di prova"
          aria-label="Notifica di prova"
        >
          <Send size={14} />
        </button>
      )}
    </div>
  );
}
